import React from 'react';
import styled, { keyframes } from 'styled-components';

interface LoadingProps {
  text?: string;
  fullScreen?: boolean;
}

// Spinning vinyl animation
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Pulsing glow animation
const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(157, 101, 201, 0.7), 0 0 20px rgba(157, 101, 201, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(157, 101, 201, 0.9), 0 0 40px rgba(157, 101, 201, 0.5);
  }
`;

// Color shift animation
const colorShift = keyframes`
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(360deg);
  }
`;

const LoadingContainer = styled.div<{ $fullScreen?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${props => props.$fullScreen ? `
    height: 100vh;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    background-color: var(--color-bg);
    z-index: 100;
  ` : `
    padding: 2rem 0;
  `}
`;

const VinylRecord = styled.div<{ $fullScreen?: boolean }>`
  width: ${props => props.$fullScreen ? '120px' : '80px'};
  height: ${props => props.$fullScreen ? '120px' : '80px'};
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    var(--color-bg-light) 15%,
    var(--color-bg-dark) 15%,
    var(--color-bg-dark) 45%,
    black 45%,
    black 48%,
    var(--color-bg-dark) 48%,
    var(--color-bg-dark) 80%,
    var(--color-bg-light) 80%
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 3s linear infinite, ${glow} 2s ease-in-out infinite, ${colorShift} 10s linear infinite;
  box-shadow: 0 0 20px rgba(157, 101, 201, 0.7);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &::before {
    content: '';
    position: absolute;
    width: ${props => props.$fullScreen ? '18px' : '12px'};
    height: ${props => props.$fullScreen ? '18px' : '12px'};
    border-radius: 50%;
    background-color: var(--color-primary);
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background:
      repeating-radial-gradient(
        var(--color-bg-dark),
        var(--color-bg-dark) 2px,
        rgba(0, 0, 0, 0.3) 3px,
        rgba(0, 0, 0, 0.3) 4px
      );
    opacity: 0.4;
  }
`;

const LoadingText = styled.h2<{ $fullScreen?: boolean }>`
  margin-top: 1.5rem;
  color: var(--color-primary);
  font-family: var(--font-family-heading);
  font-size: ${props => props.$fullScreen ? '2rem' : '1.2rem'};
  text-shadow: 0 0 10px rgba(255, 107, 107, 0.7);
  letter-spacing: 2px;

  span {
    display: inline-block;
    animation: ${glow} 1.5s ease-in-out infinite;
    animation-delay: calc(0.1s * var(--i));

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const SubText = styled.p<{ $fullScreen?: boolean }>`
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono, monospace);
  margin-top: 0.75rem;
  font-size: ${props => props.$fullScreen ? '1rem' : '0.8rem'};
  opacity: 0.8;
`;

const Loading: React.FC<LoadingProps> = ({ text = "LOADING", fullScreen = false }) => {
  // Memoize split characters to avoid array recreation on every render
  const characters = React.useMemo(() => text.split(''), [text]);

  return (
    <LoadingContainer $fullScreen={fullScreen}>
      <VinylRecord $fullScreen={fullScreen} />
      <LoadingText $fullScreen={fullScreen}>
        {characters.map((char, i) => (
          <span key={i} style={{ '--i': i } as React.CSSProperties}>
            {char}
          </span>
        ))}
      </LoadingText>
      <SubText $fullScreen={fullScreen}>Dropping the needle on some vintage vibes...</SubText>
    </LoadingContainer>
  );
};

export default Loading;